<template>
    <div class="mv-mitglieder-list" ref="mvMitgliederList">
        <h3>
            Mitgliederliste
            <b-button
                v-if="checkRight('write')"
                :size="size"
                @click="dataCreate"
            >
                Neu
                <font-awesome-icon icon="plus" />
            </b-button>
        </h3>
        <b-pagination
            v-if="pagination.step < pagination.total"
            v-model="pagination.current"
            :total-rows="pagination.total"
            :per-page="pagination.step"
            :size="size"
            align="center"
            @change="changePage"
        />
        <div class>Datensätze ingesamt: {{ pagination.total }}</div>
        <Table
            bordered
            striped
            small
            :responsive="true"
            :fields="usedFieldDescription"
            :items="items"
            :isBusy="isBusy"
            :sortBy="sortBy"
            :sortDesc="sortDesc"
            :size="size"
            busyText="Lädt Mitgliederliste..."
            emptyText="Keine Mitglieder gefunden"
            @dataSelected="detailSelected"
            @sortChanged="sortChanged"
        >
            <template v-slot:photo_id="{ row: row }">
                <ImageView :src="row.value" :width="100" :height="150" />
            </template>

            <template v-slot:contact_mail="{ row: row }">
                <a
                    v-for="contactItem in getContactFieldByType(
                        row.item.contact,
                        'mail'
                    )"
                    :key="contactItem.id"
                    :style="{ display: 'block' }"
                    :href="'mailto:' + contactItem.value"
                    >{{ contactItem.value }}</a
                >
            </template>

            <template v-slot:contact_phone="{ row: row }">
                <a
                    v-for="contactItem in getContactFieldByType(
                        row.item.contact,
                        'phone'
                    )"
                    :key="contactItem.id"
                    :style="{ display: 'block' }"
                    :href="'tel:' + contactItem.value"
                >
                    {{ contactItem.value }}
                    <template v-if="contactItem.description"
                        >({{ contactItem.description }})</template
                    >
                </a>
            </template>

            <template v-slot:contact_fax="{ row: row }">
                <a
                    v-for="contactItem in getContactFieldByType(
                        row.item.contact,
                        'fax'
                    )"
                    :key="contactItem.id"
                    :style="{ display: 'block' }"
                    :href="'tel:' + contactItem.value"
                >
                    {{ contactItem.value }}
                    <template v-if="contactItem.description"
                        >({{ contactItem.description }})</template
                    >
                </a>
            </template>

            <template v-slot:contact_website="{ row: row }">
                <a
                    v-for="contactItem in getContactFieldByType(
                        row.item.contact,
                        'website'
                    )"
                    :key="contactItem.id"
                    :style="{ display: 'block' }"
                    :href="contactItem.value"
                    >{{ contactItem.value }}</a
                >
            </template>

            <template v-slot:geburtstag="{ row: row }">
                <template v-if="row.value">{{
                    $moment(row.value).format("DD.MM.Y")
                }}</template>
                <template v-else>&nbsp;</template>
            </template>

            <template v-slot:eintritt="{ row: row }">
                <template v-if="row.value">{{
                    $moment(row.value).format("DD.MM.Y")
                }}</template>
                <template v-else>&nbsp;</template>
            </template>

            <template v-slot:austritt="{ row: row }">
                <template v-if="row.value">{{
                    $moment(row.value).format("DD.MM.Y")
                }}</template>
                <template v-else>&nbsp;</template>
            </template>

            <template v-slot:todesdatum="{ row: row }">
                <template v-if="row.value">{{
                    $moment(row.value).format("DD.MM.Y")
                }}</template>
                <template v-else>&nbsp;</template>
            </template>

            <template v-slot:statuserweiterung="{ row: row }">
                <template v-if="row.value.length > 0">
                    <p
                        v-for="sw in row.value"
                        :key="sw.bezeichnung"
                        class="statuserweiterung"
                    >
                        {{ sw.bezeichnung }}
                    </p>
                </template>
                <template v-else>-</template>
            </template>

            <template v-slot:actions="{ row: data }">
                <b-button
                    :size="size"
                    variant="link"
                    class="mv-table-action-btn mr-1"
                    @click="detailSelected(data.id)"
                >
                    <template v-if="checkRight('write')">Bearbeiten</template>
                    <template v-else>Ansehen</template>
                </b-button>
            </template>
        </Table>
        <b-pagination
            v-if="pagination.step < pagination.total"
            v-model="pagination.current"
            :total-rows="pagination.total"
            :per-page="pagination.step"
            :size="size"
            align="center"
            @change="changePage"
        />
    </div>
</template>

<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Rights from "@/mixins/Rights/Rights";
import Table from "@/components/Table/Table";
import ImageView from "@/components/ImageView/ImageView";
import Toaster from "@/mixins/Toaster/Toaster";

import "./MitgliederList.scss";

const MitgliederApi = ApiFactory.get("mitglieder");

export default {
    name: "MitgliederList",
    props: ["selectedFields", "filter"],
    components: {
        Table,
        ImageView
    },
    mixins: [Rights, Toaster],
    data() {
        return {
            fields: {
                table: {
                    id: {
                        key: "id",
                        label: "ID",
                        sortable: true
                    },
                    status: {
                        key: "status",
                        label: "Status",
                        sortable: true
                    },
                    statuserweiterung: {
                        key: "statuserweiterung",
                        label: "Statuserweiterung",
                        sortable: false
                    },
                    photo_id: {
                        key: "photo_id",
                        label: "Bild",
                        sortable: false,
                        class: "text-center"
                    },
                    anrede: { key: "anrede", label: "Anrede", sortable: false },
                    titel: { key: "titel", label: "Titel", sortable: false },
                    vorname: {
                        key: "vorname",
                        label: "Vorname",
                        sortable: true
                    },
                    name: { key: "name", label: "Name", sortable: true },
                    plz: {
                        key: "plz",
                        label: "PLZ",
                        sortable: true
                    },
                    ort: {
                        key: "ort",
                        label: "Ort",
                        sortable: true
                    },
                    contact_mail: {
                        key: "contact_mail",
                        label: "E-Mail",
                        sortable: false
                    },
                    contact_phone: {
                        key: "contact_phone",
                        label: "Telefon",
                        sortable: false
                    },
                    contact_fax: {
                        key: "contact_fax",
                        label: "Fax",
                        sortable: false
                    },
                    contact_website: {
                        key: "contact_website",
                        label: "Webseite",
                        sortable: false
                    },
                    geburtstag: {
                        key: "geburtstag",
                        label: "Geburtstag",
                        sortable: false
                    },
                    eintritt: {
                        key: "eintritt",
                        label: "Eintritt",
                        sortable: true
                    },
                    austritt: {
                        key: "austritt",
                        label: "Austritt",
                        sortable: true
                    },
                    todesdatum: {
                        key: "todesdatum",
                        label: "Todesdatum",
                        sortable: true
                    },
                    swift: {
                        key: "swift",
                        label: "IBAN",
                        sortable: true
                    },
                    bemerkung_new: {
                        key: "bemerkung_new",
                        label: "Bemerkung",
                        sortable: false
                    },
                    sp_since: {
                        key: "sp_since",
                        label: "SingPause Beginn",
                        sortable: false
                    },
                    actions: {
                        key: "actions",
                        label: "Funktionen",
                        class: "text-center"
                    }
                },
                default: [
                    "id",
                    "status",
                    "photo_id",
                    "anrede",
                    "titel",
                    "vorname",
                    "name"
                ]
            },
            items: [],
            isBusy: false,
            sortBy: "name",
            sortDesc: false,
            selected: null,
            pagination: {
                step: 100,
                total: 0,
                current: 1
            }
        };
    },
    created() {

    },
    computed: {
        usedFieldDescription() {
            var newFields = [];
            var iterator = this.fields.default;
            if (this.selectedFields !== null) {
                iterator = this.selectedFields;
            }
            iterator.forEach(field => {
                if (field in this.fields.table) {
                    newFields.push(this.fields.table[field]);
                } else {
                    newFields.push(field);
                }
            });
            newFields.push(this.fields.table["actions"]);
            return newFields;
        },
        localSelectedFields() {
            var returnFields = this.selectedFields;
            if (returnFields === null) {
                returnFields = this.fields.default;
            }
            return returnFields;
        },
        size() {
            const settings = this.$store.getters.getSettings;
            return settings.size;
        }
    },
    watch: {
        filter: {
            handler(newValue, oldValue) {
                this.fetch();
                // this.$refs.mvMitgliederList.scrollIntoView();
            }
        }
    },
    methods: {
        async fetch(page) {
            this.isBusy = true;

            let newPagination = 0;
            if(page && page > 0) {
                newPagination = page - 1;
            } else {
                this.pagination.current = 1;
            }

            const response = await MitgliederApi.get({
                fields: this.localSelectedFields,
                filter: this.filter,
                limit: this.pagination.step,
                page: newPagination,
                    // this.pagination.current > 0
                    //     ? this.pagination.current - 1
                    //     : 0,
                order: this.sortBy,
                sort: this.sortDesc ? "desc" : "asc"
            });
            if (!response.ok) {
                // todo: Errorhandling
                this.makeToast(
                    "Fehler", "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.", "danger"
                );
            }
            this.items = response.json.collection;
            this.pagination.step = response.json.pagination.step;
            this.pagination.total = response.json.pagination.total;
            this.registerRights(response);

            this.isBusy = false;
        },
        changePage(page) {
            this.pagination.current = page;
            this.fetch(page);
        },
        sortChanged(ctx) {
            this.pagination.current = 1;
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.fetch(this.pagination.current);
        },
        dataCreate() {
            MitgliederApi.abort();
            this.$router.push({
                path: `/mitglieder/new`
            });
        },
        detailSelected(id) {
            MitgliederApi.abort();
            this.$router.push({
                path: `/mitglieder/${id}`
            });
        },
        getContactFieldByType(items, type) {
            return items.filter(item => item.type == type);
        }
    }
};
</script>
